import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import ReusableTable from '../dashboard/ReusableTable';
import ReusableForm from '../common/ReusableForm';
import { Dialog } from 'primereact/dialog';
import { getRequest, postRequest, putRequest, deleteRequest } from '../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ProgressSpinner } from 'primereact/progressspinner';
import OutfittingProgressDashboard from '../dashboard/OutfittingProgressCarousel';

const initialOutfittingState = {
  project: '', 
  ship: '', 
  months: '',
  hull_structure_planned: '', 
  hull_structure_actual: '',
  hull_outfit_planned: '', 
  hull_outfit_actual: '',
  engineering_planned: '', 
  engineering_actual: '',
  piping_planned: '', 
  piping_actual: '',
  electrical_planned: '', 
  electrical_actual: '',
  weapon_planned: '', 
  weapon_actual: '',
  dry_dock_trials_planned: '', 
  dry_dock_trials_actual: '',
  comp_inspection_planned: '', 
  comp_inspection_actual: '',
  outfitting_planned: '', 
  outfitting_actual: '',
  progress_planned: '', 
  progress_actual: '',
  deficit_percentage: '', 
  monthly_planned: '',
  monthly_achieved: '', 
  deficit_percentage2: '',
  outfit_planned: '', 
  outfit_achieved: '',
  deficit_percentage3: '', 
  remarks: '', 
  status_db: 1
};

const initialImportState = {
  importFile: null,
  overwriteExisting: false
};

const numericFields = [
  'hull_structure_planned', 'hull_structure_actual',
  'hull_outfit_planned', 'hull_outfit_actual',
  'engineering_planned', 'engineering_actual',
  'piping_planned', 'piping_actual',
  'electrical_planned', 'electrical_actual',
  'weapon_planned', 'weapon_actual',
  'dry_dock_trials_planned', 'dry_dock_trials_actual',
  'comp_inspection_planned', 'comp_inspection_actual',
  'outfitting_planned', 'outfitting_actual',
  'progress_planned', 'progress_actual',
  'deficit_percentage', 'monthly_planned',
  'monthly_achieved', 'deficit_percentage2',
  'outfit_planned', 'outfit_achieved',
  'deficit_percentage3'
];

export default function OutfittingData() {
  const [records, setRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [record, setRecord] = useState(initialOutfittingState);
  const [importData, setImportData] = useState(initialImportState);
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  // Pagination state
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [selectedDashboardShip, setSelectedDashboardShip] = useState(null);


  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchRecords(lazyState.page, lazyState.rows);
  }, [lazyState.page, lazyState.rows]);

  const handleTableSearch = (value) => {};

  const onPageChange = (event) => {
    setLazyState(event);
  };

  useEffect(() => {
    if (record.project) fetchShips(record.project);
    else {
      setShips([]);
      setRecord(r => ({ ...r, ship: '' }));
    }
  }, [record.project]);

  const fetchProjects = async () => {
    try {
      const res = await getRequest('master/projects/');
      setProjects(res.results?.data || res.data || []);
    } catch {
      toast.error('Failed to fetch projects.');
    }
  };

  const fetchShips = async projectId => {
    try {
      const res = await getRequest(`master/projects/?parent_id=${projectId}`);
      setShips(res.results?.data || res.data || []);
    } catch {
      toast.error('Failed to fetch ships.');
    }
  };

  const fetchRecords = async (page = 1, pageSize = 10, shipId = null) => {
    setLoading(true);
    try {
      let url = `transaction/outfitting-data/?page=${page}`;
      if (shipId) {
        url += `&ship=${shipId}`;
      }
      
      const res = await getRequest(url);
      
      const responseData = res.results?.data || res.data || [];
      const count = res.count || responseData.length;

      const processedData = responseData.map(r => ({ 
        ...r, 
        ship_name: r.ship?.name || 'N/A',
        project_name: r.project?.name || 'N/A',
        completion_percentage: calculateCompletionPercentage(r)
      }));

      setRecords(processedData);
      setTotalRecords(count);
      setLazyState(prev => ({
        ...prev,
        first: (page - 1) * pageSize,
        page
      }));
    } catch {
      toast.error('Failed to fetch outfitting data.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (selectedDashboardShip) {
      fetchRecords(lazyState.page, lazyState.rows, selectedDashboardShip.id);
    }
  }, [selectedDashboardShip]);
  const deleteFormFields = [
    {
        name: "confirmation",
        label: "Confirm Deletion",
        type: "text",
        required: true,
        placeholder: "Type 'DELETE' to confirm",
        helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
];

  const calculateCompletionPercentage = (record) => {
    if (!record.outfitting_planned || !record.outfitting_actual) return 'N/A';
    const percentage = (record.outfitting_actual / record.outfitting_planned * 100).toFixed(1);
    return `${percentage}%`;
  };

  const openAddDialog = () => {
    setRecord(initialOutfittingState);
    setAddDialogVisible(true);
  };

  const openEditDialog = row => {
    setRecord({
      id: row.id,
      project: row.ship?.parent?.id || '',
      ship: row.ship?.id || '',
      months: row.months,
      hull_structure_planned: row.hull_structure_planned ?? '',
      hull_structure_actual: row.hull_structure_actual ?? '',
      hull_outfit_planned: row.hull_outfit_planned ?? '',
      hull_outfit_actual: row.hull_outfit_actual ?? '',
      engineering_planned: row.engineering_planned ?? '',
      engineering_actual: row.engineering_actual ?? '',
      piping_planned: row.piping_planned ?? '',
      piping_actual: row.piping_actual ?? '',
      electrical_planned: row.electrical_planned ?? '',
      electrical_actual: row.electrical_actual ?? '',
      weapon_planned: row.weapon_planned ?? '',
      weapon_actual: row.weapon_actual ?? '',
      dry_dock_trials_planned: row.dry_dock_trials_planned ?? '',
      dry_dock_trials_actual: row.dry_dock_trials_actual ?? '',
      comp_inspection_planned: row.comp_inspection_planned ?? '',
      comp_inspection_actual: row.comp_inspection_actual ?? '',
      outfitting_planned: row.outfitting_planned ?? '',
      outfitting_actual: row.outfitting_actual ?? '',
      progress_planned: row.progress_planned ?? '',
      progress_actual: row.progress_actual ?? '',
      deficit_percentage: row.deficit_percentage ?? '',
      monthly_planned: row.monthly_planned ?? '',
      monthly_achieved: row.monthly_achieved ?? '',
      deficit_percentage2: row.deficit_percentage2 ?? '',
      outfit_planned: row.outfit_planned ?? '',
      outfit_achieved: row.outfit_achieved ?? '',
      deficit_percentage3: row.deficit_percentage3 ?? '',
      remarks: row.remarks || '',
      status_db: row.status_db
    });
    setEditDialogVisible(true);
  };

  const openImportDialog = () => {
    setImportData(initialImportState);
    setImportDialogVisible(true);
  };

  const handleRowSelect = (row) => {
    setSelectedRecord(row);
  };

  const saveRecord = async () => {
    if (!record.project || !record.ship || !record.months) {
      toast.error('Please select project, ship and enter months.');
      return;
    }
    
    const payload = { ...record };
    numericFields.forEach(f => {
      const v = payload[f];
      payload[f] = v === '' || v == null ? null : Number(v);
    });
    
    try {
      if (payload.id) {
        await putRequest(`transaction/outfitting-data/${payload.id}/`, payload);
        toast.success('Record updated.');
      } else {
        await postRequest('transaction/outfitting-data/', payload);
        toast.success('Record added.');
      }
      fetchRecords(lazyState.page, lazyState.rows);
      setAddDialogVisible(false);
      setEditDialogVisible(false);
    } catch {
      toast.error('Failed to save record.');
    }
  };

  const openDeleteModal = (row) => {
    setRecordToDelete(row);
    setDeleteFormData({ confirmation: '' });
    setDeleteDialogVisible(true);
  };

  const confirmDelete = async () => {
    if (deleteFormData.confirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    if (!recordToDelete) return;

    try {
      setLoading(true);
      await deleteRequest(`transaction/outfitting-data/${recordToDelete.id}/`);
      toast.success('Record deleted successfully.');
      fetchRecords(lazyState.page, lazyState.rows);
    } catch (error) {
      toast.error('Failed to delete record: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setDeleteDialogVisible(false);
      setRecordToDelete(null);
      setDeleteFormData({ confirmation: '' });
    }
  };

  const handleImportSubmit = async () => {
    if (!importData.importFile) {
      toast.error('Please select a file to import.');
      return;
    }

    try {
      setImportLoading(true);
      const formData = new FormData();
      formData.append('file', importData.importFile);

      if (importData.overwriteExisting) {
        formData.append('overwrite_existing', 'true');
      }

      const response = await postRequest('transaction/import-outfitting/', formData, true);
      const data = await response.data;
      
      setImportDialogVisible(false);

      if (data.error_file_path) {
        const link = document.createElement('a');
        link.href = `http://pms-api.ilizien-projects-cdf.in${data.error_file_path}`;
        link.download = data.error_file_path.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      if (data.status === 'success') {
        toast.success(data.message || 'Import successful.');
        fetchRecords(lazyState.page, lazyState.rows);
      } else if (data.status === 'partial_success') {
        toast.warning(data.message || 'Import completed with some errors.');
        fetchRecords(lazyState.page, lazyState.rows);
      } else if (data.status === 'error') {
        toast.error(data.message || 'Import failed.');
      } else {
        toast.error('Unexpected response from server.');
      }

    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.message || 'Failed to process import.');
      setImportDialogVisible(false);
    } finally {
      setImportLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    window.open('https://pms-api.ilizien-projects-cdf.in/media/sample-csv-outfitted.csv', '_blank');
  };
  
  const getFormFields = () => [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(p => ({ label: p.name, value: p.id })),
      required: true
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      options: ships.map(s => ({ label: s.name, value: s.id })),
      required: true
    },
    { name: 'months', label: 'Months', type: 'text', required: true },
    
    // Hull Structure
    { name: 'hull_structure_planned', label: 'Hull Structure Planned', type: 'number' },
    { name: 'hull_structure_actual', label: 'Hull Structure Actual', type: 'number' },
    
    // Hull Outfit
    { name: 'hull_outfit_planned', label: 'Hull Outfit Planned', type: 'number' },
    { name: 'hull_outfit_actual', label: 'Hull Outfit Actual', type: 'number' },
    
    // Engineering
    { name: 'engineering_planned', label: 'Engineering Planned', type: 'number' },
    { name: 'engineering_actual', label: 'Engineering Actual', type: 'number' },
    
    // Piping
    { name: 'piping_planned', label: 'Piping Planned', type: 'number' },
    { name: 'piping_actual', label: 'Piping Actual', type: 'number' },
    
    // Electrical
    { name: 'electrical_planned', label: 'Electrical Planned', type: 'number' },
    { name: 'electrical_actual', label: 'Electrical Actual', type: 'number' },
    
    // Weapon
    { name: 'weapon_planned', label: 'Weapon Planned', type: 'number' },
    { name: 'weapon_actual', label: 'Weapon Actual', type: 'number' },
    
    // Dry Dock Trials
    { name: 'dry_dock_trials_planned', label: 'Dry Dock Trials Planned', type: 'number' },
    { name: 'dry_dock_trials_actual', label: 'Dry Dock Trials Actual', type: 'number' },
    
    // Comp Inspection
    { name: 'comp_inspection_planned', label: 'Comp Inspection Planned', type: 'number' },
    { name: 'comp_inspection_actual', label: 'Comp Inspection Actual', type: 'number' },
    
    // Outfitting
    { name: 'outfitting_planned', label: 'Outfitting Planned', type: 'number' },
    { name: 'outfitting_actual', label: 'Outfitting Actual', type: 'number' },
    
    // Progress
    { name: 'progress_planned', label: 'Progress Planned', type: 'number' },
    { name: 'progress_actual', label: 'Progress Actual', type: 'number' },
    
    // Deficit Percentages
    { name: 'deficit_percentage', label: 'Deficit Percentage', type: 'number' },
    { name: 'deficit_percentage2', label: 'Deficit Percentage 2', type: 'number' },
    { name: 'deficit_percentage3', label: 'Deficit Percentage 3', type: 'number' },
    
    // Monthly
    { name: 'monthly_planned', label: 'Monthly Planned', type: 'number' },
    { name: 'monthly_achieved', label: 'Monthly Achieved', type: 'number' },
    
    // Outfit
    { name: 'outfit_planned', label: 'Outfit Planned', type: 'number' },
    { name: 'outfit_achieved', label: 'Outfit Achieved', type: 'number' },
    
    // Remarks
    { name: 'remarks', label: 'Remarks', type: 'text' }
  ];

  const columnFields = [
    { field: 'ship_name', header: 'Ship', sortable: true },
    { field: 'months', header: 'Month', sortable: true },
    { field: 'hull_structure_planned', header: 'Hull Structure Planned', sortable: true },
    { field: 'hull_structure_actual', header: 'Hull Structure Actual', sortable: true },
    { field: 'hull_outfit_planned', header: 'Hull Outfit Planned', sortable: true },
    { field: 'hull_outfit_actual', header: 'Hull Outfit Actual', sortable: true },
    { field: 'engineering_planned', header: 'Engineering Planned', sortable: true },
    { field: 'engineering_actual', header: 'Engineering Actual', sortable: true },
    { field: 'piping_planned', header: 'Piping Planned', sortable: true },
    { field: 'piping_actual', header: 'Piping Actual', sortable: true },
    { field: 'electrical_planned', header: 'Electrical Planned', sortable: true },
    { field: 'electrical_actual', header: 'Electrical Actual', sortable: true },
    { field: 'weapon_planned', header: 'Weapon Planned', sortable: true },
    { field: 'weapon_actual', header: 'Weapon Actual', sortable: true }
  ];

  const actionItems = [
    { label: 'Edit', icon: 'pi pi-pencil', command: openEditDialog },
    { label: 'Delete', icon: 'pi pi-trash', command: openDeleteModal }
  ];

  const importDialogFooter = (
    <div className="flex justify-end gap-3 mt-6">
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setImportDialogVisible(false)} disabled={importLoading} />
      <Button
        label={importLoading ? 'Uploading...' : 'Upload'}
        icon={!importLoading ? 'pi pi-upload' : null}
        className="p-button-primary"
        onClick={handleImportSubmit}
        disabled={!importData.importFile || importLoading}
      />
    </div>
  );

  const deleteDialogFooter = (
    <div className="flex justify-end gap-3 mt-6">
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialogVisible(false)} disabled={loading} />
      <Button
        label={loading ? 'Deleting...' : 'Delete'}
        icon={!loading ? 'pi pi-trash' : null}
        className="p-button-primary"
        onClick={confirmDelete}
        disabled={!deleteFormData.confirmation || loading}
      />
    </div>
  );

  return (
    <div className="p-4">
      <ToastContainer />
      <div className="flex flex-col gap-4">
        <div className="w-full">
          <OutfittingProgressDashboard 
            selectedRecord={selectedRecord}
            onShipSelect={setSelectedDashboardShip}
          />
        </div>
        {selectedDashboardShip && records.length === 0 && !loading && (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-gray-600">No data available for the selected ship</p>
          </div>
        )}
        
        {(!selectedDashboardShip || records.length > 0) && (
          <div className="w-full">
            <ReusableTable
              title={selectedDashboardShip 
                ? `Outfitting Data for ${selectedDashboardShip.name}` 
                : "Outfitting Data (All Ships)"}
              data={records}
              columnFields={columnFields}
              // actionMenu={true}
              // actionItems={actionItems}
              loading={loading}
              // setAddNewDialogVisible={openAddDialog}
              // onImport={openImportDialog}
              totalRecords={totalRecords}
              onPageChange={onPageChange}
              first={lazyState.first}
              rows={lazyState.rows}
              onRowSelect={handleRowSelect}
              selectionMode="single"
              selectedRecord={selectedRecord}
              searchable={false}
              className= "outfit"
            />
          </div>
        )}
      </div>

      <Dialog
        visible={addDialogVisible || editDialogVisible}
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        header={editDialogVisible ? 'Edit Outfitting Data' : 'Add Outfitting Data'}
        onHide={() => { setAddDialogVisible(false); setEditDialogVisible(false); }}
        style={{ width: '90vw', maxHeight: '90vh' }}
        maximizable
      >
        <ReusableForm
          formData={record}
          setFormData={setRecord}
          fields={getFormFields()}
          onSubmit={saveRecord}
          onHide={() => { setAddDialogVisible(false); setEditDialogVisible(false); }}
          columnLayout={3}
        />
      </Dialog>

      <Dialog
        visible={importDialogVisible}
        header="Import Outfitting Data"
        onHide={() => !importLoading && setImportDialogVisible(false)}
        style={{ width: '40vw' }}
        footer={importDialogFooter}
        closable={!importLoading}
      >
        <div className="space-y-4">
          <p className="text-gray-700">Upload a file in Excel (.xlsx) or CSV format. If the upload fails due to validation errors, an error file will be downloaded automatically.</p>

          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-blue-600">
              <Button
                label="Download Sample CSV"
                icon="pi pi-download"
                className="p-button-text p-button-info"
                onClick={downloadSampleCSV}
                disabled={importLoading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="importFile" className="block font-medium mb-1">Select File</label>
            <input
              type="file"
              id="importFile"
              accept=".xlsx,.csv"
              className="w-full"
              onChange={e => setImportData(prev => ({ ...prev, importFile: e.target.files[0] }))}
              disabled={importLoading}
            />
            <small className="text-gray-500">Supported formats: .xlsx, .csv</small>
          </div>

          {importLoading && (
            <div className="flex justify-center">
              <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={deleteDialogVisible}
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        header="Confirm Deletion"
        onHide={() => setDeleteDialogVisible(false)}
        style={{ width: '30vw' }}
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center mb-5">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <i className="pi pi-exclamation-triangle text-3xl text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Confirm Deletion
            </h3>
            <p className="text-gray-600">
              Are you sure you want to delete this outfitting data?
            </p>
          </div>
          {recordToDelete && (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
              <p className="font-medium text-gray-800 mb-1">
                Ship: <span className="text-blue-600">{recordToDelete.ship_name}</span>
              </p>
              <p className="text-sm text-gray-600">
                This action cannot be undone and may affect related operations.
              </p>
            </div>
          )}
          <ReusableForm
            formData={deleteFormData}
            setFormData={setDeleteFormData}
            fields={deleteFormFields}
            onSubmit={confirmDelete}
            onHide={() => setDeleteDialogVisible(false)}
            loading={loading}
          />
        </div>
      </Dialog>
    </div>
  );
}